import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { GenericContentModel } from '@/common/models/genericContent'
import { calculateItemsPerPage } from '@/common/utils/calculateItemsPerPage'
import { api, ApiError, APIPaginated, ApiWhere } from '@/lib/axios'
import { FilterGenericContentForm } from '../schemas/FilterGenericContentSchema'
import { useFilterGenericContent } from './useFilterGenericContent'

type GetPaginateGenericContentProps = {
  page: number
  orderBy: OrderBy
  filters: FilterGenericContentForm
}

async function getPaginateGenericContent({
  page,
  orderBy,
  filters,
}: GetPaginateGenericContentProps) {
  const where: ApiWhere = {
    isActive: !filters.isActive,
    ...(filters.freeSearch && {
      OR: [
        { tagContent: { contains: filters.freeSearch } },
        { fieldKey: { contains: filters.freeSearch } },
      ],
    }),
    ...(filters.contentType && {
      contentType: { array_contains: filters.contentType },
    }),
    ...(filters.relativeKeys && {
      relativeKeys: { array_contains: filters.relativeKeys },
    }),
    ...(filters.searchTags && {
      searchTags: { array_contains: filters.searchTags },
    }),
  }

  const { data } = await api.backend.get<APIPaginated<GenericContentModel>>(
    '/generic-content/paginate',
    {
      params: {
        page,
        limit: 25,
        relations: {
          orderBy,
          where,
        },
      },
    },
  )

  return data
}

export function useGenericContent() {
  const [tableSearch, setTableSearch] = useState('')
  const [tableSearchResult, setTableSearchResult] = useState<
    GenericContentModel[]
  >([])
  const [page, setPage] = useState(1)
  const [filters] = useFilterGenericContent()

  const debouncedTableSearch = useDebounce(tableSearch, 300)
  const { t } = useTranslation('settings')
  const { orderBy, handleChangeOrder } = useOrderBy()
  const { data, isLoading, error } = useQuery<
    APIPaginated<GenericContentModel>,
    AxiosError<ApiError>
  >({
    queryKey: [
      'generic-content/paginate',
      page,
      orderBy,
      ...Object.values(filters),
    ],
    queryFn: () => getPaginateGenericContent({ page, orderBy, filters }),
  })

  const handleGenericContentSearch = (search: string) => {
    setTableSearch(search)
  }

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  const genericContentBadges = useMemo(() => {
    return {
      tagContent: new Set(
        data?.items?.map((register) => register.tagContent) || [],
      ).size.toFixed(0),
    }
  }, [data])

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('generic_content.use_generic_content.toast_error.title'),
        description:
          error?.response?.data.friendlyMessage ||
          t('generic_content.use_generic_content.toast_error.description'),
      },
    })
  }, [error, t])

  useEffect(() => {
    if (!data || data?.items?.length === 0) return

    const searchTerm = debouncedTableSearch.toLowerCase()
    const filteredData = data.items.filter(
      (content) =>
        content.contentType.some((type) =>
          type.toLowerCase().includes(searchTerm),
        ) ||
        content.fieldKey.toLowerCase().includes(searchTerm) ||
        content.fieldValue.toLowerCase().includes(searchTerm) ||
        content.infoMessage.toLowerCase().includes(searchTerm) ||
        content.tagContent.toLowerCase().includes(searchTerm),
    )

    setTableSearchResult(filteredData)
  }, [data, debouncedTableSearch, t])

  return {
    genericContent: debouncedTableSearch
      ? tableSearchResult
      : (data?.items ?? []),
    genericContentOrderBy: orderBy,
    genericContentPaginated: data?.meta,
    isLoadingGenericContent: isLoading,
    genericContentBadges,
    filtersQty: Object.values(filters).filter(Boolean).length,
    contentsPerPage: calculateItemsPerPage(data?.meta),
    handleGenericContentSearch,
    handleChangePage,
    handleChangeOrder,
  }
}
